import { rem } from '@mantine/core';
import type { ExternalGroupRow } from '../api/externalGroups';
import { useSession } from '../api/session';
import { ViewSwitch, type RosterView } from './ViewSwitch';
import { hoursLabel } from './derive';
import { ini, plEk, plOs } from '../lib/ui';
import { INPUT } from './ui';

export interface TeamRow {
  group: ExternalGroupRow;
  minutes: number;
}

/** The teams rail: who covers posts for us without being ours to brief. */
export function TeamsSidebar(p: {
  rows: TeamRow[];
  search: string;
  setSearch: (v: string) => void;
  groupId: string | null;
  pick: (id: string) => void;
  newTeam: () => void;
  peopleCount: number;
  setView: (v: RosterView) => void;
}) {
  const shown = p.rows.filter(({ group: g }) => {
    const q = p.search.trim().toLowerCase();
    if (!q) return true;
    return (
      g.name.toLowerCase().includes(q) ||
      g.leaderName.toLowerCase().includes(q) ||
      g.slots.some((s) => s.externalPersonName.toLowerCase().includes(q))
    );
  });
  const canEdit = useSession().can('plan:write');
  const totalSlots = p.rows.reduce((a, r) => a + r.group.slots.length, 0);
  const totalMin = p.rows.reduce((a, r) => a + r.minutes, 0);

  return (
    <div
      style={{
        width: 306,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'oklch(1 0 0)',
        borderRight: '1px solid oklch(0.9 0.006 95)',
        minHeight: 0,
      }}
    >
      <ViewSwitch
        view="ekipe"
        setView={p.setView}
        people={p.peopleCount}
        teams={p.rows.length}
      />

      <div
        style={{
          padding: '12px 12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderBottom: '1px solid oklch(0.94 0.005 95)',
        }}
      >
        <input
          value={p.search}
          onChange={(e) => p.setSearch(e.currentTarget.value)}
          placeholder="Išči ekipo ali vodjo…"
          style={{
            ...INPUT,
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 10px',
            borderRadius: 7,
            background: 'oklch(0.985 0.004 95)',
          }}
        />
        {canEdit ? (
          <button
            type="button"
            onClick={p.newTeam}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              cursor: 'pointer',
              padding: 8,
              borderRadius: 7,
              border: '1px solid oklch(0.85 0.05 245)',
              background: 'oklch(0.965 0.025 245)',
              color: 'oklch(0.4 0.08 245)',
              fontSize: rem(13.5),
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            + Dodaj ekipo
          </button>
        ) : null}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
        {shown.map(({ group: g, minutes }) => {
          const on = p.groupId === g.id;
          const people = g.slots.length;
          const meta = [
            g.leaderName || 'brez vodje',
            plOs(people),
            minutes ? hoursLabel(minutes) : 'brez izmen',
          ].join(' · ');
          return (
            <div
              key={g.id}
              onClick={() => p.pick(g.id)}
              style={{
                display: 'flex',
                gap: 9,
                alignItems: 'center',
                padding: '8px 10px',
                borderRadius: 8,
                cursor: 'pointer',
                background: on ? 'oklch(0.965 0.02 245)' : 'oklch(1 0 0)',
                border: `1px solid ${on ? 'oklch(0.86 0.04 245)' : 'oklch(0.96 0.005 95)'}`,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  // Square, like every external mark in this app (SPEC §1.1J).
                  borderRadius: 5,
                  background: 'oklch(0.94 0.008 95)',
                  color: 'oklch(0.4 0.014 90)',
                  fontSize: rem(11),
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {ini(g.name)}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: rem(13.5),
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {g.name}
                </div>
                <div style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>{meta}</div>
              </div>
            </div>
          );
        })}
        {shown.length === 0 && p.search.trim() ? (
          <div
            style={{ padding: '14px 10px', fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}
          >
            Nobena ekipa se ne ujema.
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: '9px 12px',
          borderTop: '1px solid oklch(0.94 0.005 95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: rem(11.5),
          color: 'oklch(0.6 0.012 90)',
        }}
      >
        <span>
          {plEk(p.rows.length)} · {plOs(totalSlots)}
        </span>
        <span style={{ fontFamily: 'var(--rr-font-mono)' }}>{hoursLabel(totalMin)}</span>
      </div>
    </div>
  );
}
